import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  HiOutlineXMark,
  HiOutlineArrowLeft,
  HiOutlineCheckCircle,
} from "react-icons/hi2";
import {
  authService,
  rcaService,
  plantService,
  masterDataService,
} from "../services";
import PageLoading from "../components/common/PageLoading";
import "./FishboneAnalysis.css";

const FishboneAnalysis = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rcaId = searchParams.get("rcaId");
  const currentUser = authService.getUser();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [plants, setPlants] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const tenantId = authService.getTenantId();

  const [fishbone, setFishbone] = useState({
    man: "",
    machine: "",
    method: "",
    material: "",
    environment: "",
    measurement: "",
  });

  const [formData, setFormData] = useState({
    problemStatement: "",
    plantId: "",
    machineId: "",
  });

  // Helper function to extract category from text
  const extractCategory = (text, categoryName) => {
    const regex = new RegExp(`${categoryName}:\\s*([^\\n]+)`, "i");
    const match = text.match(regex);
    return match ? match[1].trim() : "";
  };

  useEffect(() => {
    if (tenantId) {
      loadInitialData();
    }
    if (rcaId) {
      loadRcaData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcaId, tenantId]);

  const loadInitialData = async () => {
    try {
      const [plantsData, statusesData] = await Promise.all([
        plantService.getAllPlants(),
        masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
      ]);
      setPlants(plantsData || []);
      const rcaStatuses = statusesData.filter(
        (s) => !s.statusType || s.statusType === "RCA",
      );
      setStatuses(rcaStatuses);

      // Pre-fill default plant for new records
      // Auto-select plant if only 1 exists (for new records)
      if (!rcaId && plantsData?.length === 1) {
        setFormData(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load data");
    }
  };

  const loadRcaData = async () => {
    try {
      setLoading(true);
      const record = await rcaService.getRcaRecordById(rcaId);

      setFormData({
        problemStatement: record.problemDesc || "",
        plantId: record.plantId?.toString() || "",
        machineId: record.machineId?.toString() || "",
      });

      // Parse fishbone data from rootCause if it exists
      if (record.rootCause) {
        const fishboneMatch = record.rootCause.match(
          /Fishbone Analysis:\s*([\s\S]*?)(?:\n\n|$)/,
        );
        if (fishboneMatch) {
          const fishboneText = fishboneMatch[1];
          const categories = {
            man: extractCategory(fishboneText, "Man"),
            machine: extractCategory(fishboneText, "Machine"),
            method: extractCategory(fishboneText, "Method"),
            material: extractCategory(fishboneText, "Material"),
            environment: extractCategory(fishboneText, "Environment"),
            measurement: extractCategory(fishboneText, "Measurement"),
          };
          setFishbone(categories);
        }
      }
    } catch (err) {
      console.error("Error loading RCA data:", err);
      setError("Failed to load RCA data");
    } finally {
      setLoading(false);
    }
  };

  const handleFishboneChange = (name, value) => {
    setFishbone((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormDataChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      setFormErrors({});

      // Validate plantId for new records
      if (!rcaId) {
        if (!formData.plantId || formData.plantId === "") {
          setFormErrors({ plantId: "Plant is required" });
          setSaving(false);
          return;
        }
        const plantIdValue = parseInt(formData.plantId, 10);
        if (isNaN(plantIdValue) || plantIdValue <= 0) {
          setFormErrors({ plantId: "Please select a valid plant" });
          setSaving(false);
          return;
        }
      }

      const fishboneText = Object.entries(fishbone)
        .filter(([, v]) => v && v.trim().length > 0)
        .map(([k, v]) => {
          const label = k.charAt(0).toUpperCase() + k.slice(1);
          return `${label}: ${v.trim()}`;
        })
        .join("\n");

      // Ensure rootCause is not empty - if no categories filled, use a default message
      if (!fishboneText || fishboneText.trim().length === 0) {
        setError("Please fill at least one category in the Fishbone analysis");
        setSaving(false);
        return;
      }

      if (rcaId) {
        // Update existing RCA record
        const record = await rcaService.getRcaRecordById(rcaId);
        const updatedRootCause = record.rootCause
          ? record.rootCause.replace(/Fishbone Analysis:[\s\S]*?(?=\n\n|$)/, "")
          : "";

        const newRootCause = updatedRootCause.trim()
          ? `${updatedRootCause.trim()}\n\nFishbone Analysis:\n${fishboneText}`
          : `Fishbone Analysis:\n${fishboneText}`;

        // Only send the fields that the backend expects
        const updatePayload = {
          rcaId: parseInt(rcaId, 10),
          plantId: record.plantId,
          machineId: record.machineId,
          triggerReasonId: record.triggerReasonId,
          triggerReason: record.triggerReason || "", // Database requires NOT NULL
          problemDesc: record.problemDesc,
          rootCause: newRootCause,
          method: record.method === "5 Whys" ? "5 Whys + Fishbone" : "Fishbone",
          statusId:
            record.statusId || (statuses.length > 0 ? statuses[0].statusId : 1),
          createdBy: record.createdBy,
          approvedBy: record.approvedBy,
        };

        await rcaService.updateRcaRecord(rcaId, updatePayload);

        setSuccess("Fishbone analysis saved successfully!");
      } else {
        // Create new RCA record with fishbone analysis
        const plantIdValue = parseInt(formData.plantId, 10);

        // Get default status - use first available RCA status or default to 1
        const defaultStatus = statuses.length > 0 ? statuses[0].statusId : 1;

        const problemDesc = formData.problemStatement?.trim();
        if (!problemDesc || problemDesc.length === 0) {
          setError("Problem statement is required");
          setSaving(false);
          return;
        }

        const payload = {
          plantId: plantIdValue,
          machineId: formData.machineId
            ? parseInt(formData.machineId, 10)
            : null,
          triggerReason: "", // Database requires NOT NULL, so use empty string
          problemDesc: problemDesc,
          rootCause: `Fishbone Analysis:\n${fishboneText}`,
          method: "Fishbone",
          statusId: defaultStatus,
          createdBy: currentUser?.userId || 0,
        };

        console.log("Creating RCA with payload:", payload); // Debug log

        await rcaService.createRcaRecord(payload);
        setSuccess("Fishbone analysis saved successfully!");
      }

      setTimeout(() => {
        navigate("/home/rca");
      }, 1500);
    } catch (err) {
      console.error("Error saving fishbone analysis:", err);
      console.error("Error details:", err.response?.data); // More detailed logging

      const backendErrors = err.response?.data?.errors;
      if (backendErrors) {
        const newFormErrors = {};
        for (const key in backendErrors) {
          if (Object.hasOwnProperty.call(backendErrors, key)) {
            const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
            newFormErrors[fieldName] = backendErrors[key][0];
          }
        }
        setFormErrors(newFormErrors);
        setError("Please correct the highlighted errors.");
      } else {
        // Show more detailed error message
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to save fishbone analysis. Please check all required fields are filled.";
        setError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fishbone-analysis-page pagecontainer">
        <PageLoading message="Loading fishbone…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer fishbone-analysis-page">
      <div className="page-header">
        <div className="header-content">
          <button
            className="btn-back nowrap"
            onClick={() => navigate("/home/rca")}
          >
            <HiOutlineArrowLeft />
            Back to RCA
          </button>
          <h2>Fishbone Diagram Analysis</h2>
          <p>
            Identify root causes by analyzing six categories:
            <br /> Man, Machine, Method, Material, Environment, and Measurement
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate("/home/rca")}
          >
            <HiOutlineXMark />
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              "Saving..."
            ) : (
              <>
                <HiOutlineCheckCircle />
                Save Analysis
              </>
            )}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="fishbone-container">
        {/* Plant Selection - Required for new records */}
        {!rcaId && (
          <div className="fishbone-form-section">
            <div className="form-group">
              <label htmlFor="plantId">
                Plant <span className="required">*</span>
              </label>
              <select
                id="plantId"
                name="plantId"
                value={formData.plantId}
                onChange={(e) =>
                  handleFormDataChange("plantId", e.target.value)
                }
                className={formErrors.plantId ? "error" : ""}
              >
                <option value="">Select Plant</option>
                {plants.map((p) => (
                  <option key={p.plantId} value={p.plantId}>
                    {p.plantName}
                  </option>
                ))}
              </select>
              {formErrors.plantId && (
                <span className="field-error">{formErrors.plantId}</span>
              )}
            </div>
          </div>
        )}

        <div className="fishbone-diagram">
          <div className="fishbone-problem">
            <h3>Problem Statement</h3>
            {rcaId ? (
              <p>
                {formData.problemStatement || "No problem statement available"}
              </p>
            ) : (
              <textarea
                value={formData.problemStatement}
                onChange={(e) =>
                  handleFormDataChange("problemStatement", e.target.value)
                }
                placeholder="Enter the problem statement here..."
                rows={3}
              />
            )}
          </div>

          <div className="fishbone-categories">
            <div className="fishbone-category fishbone-man">
              <div className="category-header">
                <h4>Man (People)</h4>
                <span className="category-icon">👤</span>
              </div>
              <textarea
                value={fishbone.man}
                onChange={(e) => handleFishboneChange("man", e.target.value)}
                placeholder="Skills, training, staffing, human errors, knowledge..."
                rows={4}
              />
            </div>

            <div className="fishbone-category fishbone-machine">
              <div className="category-header">
                <h4>Machine (Equipment)</h4>
                <span className="category-icon">⚙️</span>
              </div>
              <textarea
                value={fishbone.machine}
                onChange={(e) =>
                  handleFishboneChange("machine", e.target.value)
                }
                placeholder="Breakdowns, settings, wear & tear, maintenance..."
                rows={4}
              />
            </div>

            <div className="fishbone-category fishbone-method">
              <div className="category-header">
                <h4>Method (Process)</h4>
                <span className="category-icon">📋</span>
              </div>
              <textarea
                value={fishbone.method}
                onChange={(e) => handleFishboneChange("method", e.target.value)}
                placeholder="Procedures, work instructions, sequence, standards..."
                rows={4}
              />
            </div>

            <div className="fishbone-category fishbone-material">
              <div className="category-header">
                <h4>Material</h4>
                <span className="category-icon">📦</span>
              </div>
              <textarea
                value={fishbone.material}
                onChange={(e) =>
                  handleFishboneChange("material", e.target.value)
                }
                placeholder="Raw material, specs, quality, handling..."
                rows={4}
              />
            </div>

            <div className="fishbone-category fishbone-environment">
              <div className="category-header">
                <h4>Environment</h4>
                <span className="category-icon">🌡️</span>
              </div>
              <textarea
                value={fishbone.environment}
                onChange={(e) =>
                  handleFishboneChange("environment", e.target.value)
                }
                placeholder="Temperature, humidity, workplace conditions, lighting..."
                rows={4}
              />
            </div>

            <div className="fishbone-category fishbone-measurement">
              <div className="category-header">
                <h4>Measurement</h4>
                <span className="category-icon">📊</span>
              </div>
              <textarea
                value={fishbone.measurement}
                onChange={(e) =>
                  handleFishboneChange("measurement", e.target.value)
                }
                placeholder="Gauges, inspection, data accuracy, calibration..."
                rows={4}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FishboneAnalysis;
